import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useGlobalStore } from "../../api/globalStores";
import IconExit from "../../Common/Icons/IconExit";
import Switch from "./Form/Switch";
import SlideDrawerItem from "./SlideDrawerItem";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useCallback, useRef } from "react";

export interface ISlideDrawer {}

function SortableEngagementPanels({
  scrollViewRef,
}: {
  scrollViewRef: React.RefObject<HTMLDivElement>;
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const globalStores = useGlobalStore();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 20,
      },
    })
  );

  const [scrolledAlready, setScrolledAlready] = useState<boolean>(false);

  const items = globalStores.curModule.engagementPanels
    .map((ePanel) => ePanel.id!)
    .filter((id) => id !== undefined);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && over?.id) {
      const ePanelToMove: number = +active.id;
      const endIndex = items.indexOf(+over.id);
      let ePanelBefore: number | undefined;
      const previousBefore = items?.[endIndex - 1];
      if (previousBefore !== ePanelToMove) {
        //Then the epanel that needs to be before the current one is at the minus one index
        ePanelBefore = previousBefore;
      } else {
        //This is the edge case where the panel that is supposed to be before is now the current one, so we arrange it after the next panel instead
        ePanelBefore = items[endIndex];
      }
      await globalStores.curModule.reorderAfter(ePanelBefore, ePanelToMove);
    }
  };

  const reorderPanels = (event: DragEndEvent) => {
    toast.promise(handleDragEnd(event), {
      success: "Panels Reordered",
      loading: "Reordering Panels",
      error: (e) => e.message,
    });
  };

  //quickfix to solve refresh scroll issue
  useEffect(() => {
    // console.log("did the useeffect run?");
    if (!scrolledAlready) {
      for (var index = 0; index < items.length; index++) {
        const itemId = items[index];
        if (itemId === +searchParams.get("epanel")!) {
          // console.log("this passed");
          if (scrollViewRef.current) {
            // scrollViewRef.current.scrollTo({
            //   top: scrollViewRef.current.scrollHeight,
            // });
            // console.log("the scroll thing ran");
            const fullHeight = scrollViewRef.current.scrollHeight;
            const itemHeight = fullHeight / items.length;
            const viewHeight = scrollViewRef.current.clientHeight;
            const targetPosition = itemHeight * index;

            scrollViewRef.current.scrollTo({
              top: targetPosition,
              behavior: "smooth",
            });
            setScrolledAlready(true);
            break;
          }
        }
      }
    }
  }, [searchParams.get("epanel"), scrollViewRef, items, setScrolledAlready]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={reorderPanels}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((itemId, idx) => {
          const ePanel = globalStores.curModule.engagementPanels.find(
            (ePanel) => ePanel.id === itemId
          );
          if (!ePanel || !ePanel.id) return <></>;
          return (
            <SlideDrawerItem
              key={ePanel.id}
              id={ePanel.id}
              position={idx}
              epType={ePanel.type ?? "MCQ"}
              isInEditMode
              onClick={() => {
                setSearchParams({
                  ...Object.fromEntries(Array.from(searchParams.entries())),

                  epanel: String(ePanel.id),
                });
                //TODO: Prevent needing to refresh entire page to ensure correct data
                // navigate(0);
                setScrolledAlready(false);
              }}
            />
          );
        })}
      </SortableContext>
    </DndContext>
  );
}

function SlideDrawer() {
  const navigate = useNavigate();
  const globalStores = useGlobalStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const scrollViewRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({
        top: scrollViewRef.current.scrollHeight,
      });
    }
  }, [scrollViewRef]);

  return (
    <div className="p-5 gap-4 bg-brij-Mud inline-flex flex-col items-start justify-start h-full">
      <div
        className={`gap-2 inline-flex flex-col items-start text-left font-['Poppins'] text-[rgba(255,254,252,1)]`}
      >
        <div className="w-full leading-none relative">
          <p className="text-2xl font-semibold leading-normal inline m-0">
            Level {globalStores.curModule.level.hskDifficulty}
          </p>
          <br />
          <p className="text-xl font-medium inline m-0 leading-[1.4px]">
            ⮡ {globalStores.curModule.topic.name}
          </p>
        </div>
        <p className="w-full text-sm font-normal m-0">
          {globalStores.curModule.module.name}
        </p>
      </div>
      <div className="gap-3 inline-flex w-full items-center">
        <p className="font-poppins font-semibold text-small text-brij-White flex-1">
          Mark Module as Complete
        </p>

        <Switch
          defaultVal={globalStores.curModule.module.isCompleted}
          onChange={(newVal) => {
            globalStores.curModule.setCompleted(newVal);
          }}
        />
      </div>
      <div className="flex items-center justify-between w-full">
        <div className="flex-col items-start justify-start w-full">
          <p className="font-poppins text-large font-semibold text-brij-White">
            Panels
          </p>
          <p className="font-poppins text-xsmall text-brij-White">
            Drag to reorder
          </p>
        </div>
        <div>
          <AddButton
            onClick={() => {
              toast.promise(globalStores.curModule.addEngagementPanel(), {
                loading: "Adding Engagement Panel",
                success: (ePanel: any) => {
                  setSearchParams({
                    ...Object.fromEntries(Array.from(searchParams.entries())),

                    epanel: String(ePanel.engagementPanel.id),
                  });
                  //TODO: Prevent needing page reload
                  navigate(0);
                  scrollToBottom();
                  return "Engagement Panel Added";
                },
                error: (e: any) => {
                  return e.message;
                },
              });
            }}
          />
        </div>
      </div>
      <div
        ref={scrollViewRef}
        className="h-full flex flex-col overflow-y-scroll gap-3 px-2"
        style={{ scrollBehavior: "auto" }}
      >
        <SortableEngagementPanels scrollViewRef={scrollViewRef} />
      </div>
      <div
        className="container gap-2.5 inline-flex flex-col items-center justify-end cursor-pointer"
        onClick={() => {
          if (
            globalStores.teacherStore.teacherAccount.id ===
            globalStores.curEPanel.engagementPanel.currentPanelEditor
          ) {
            globalStores.curEPanel.setCurrentPanelEditor(undefined, null);
          }
          navigate("/teacher/lesson_planning");
        }}
      >
        <div className="gap-4 inline-flex items-center justify-center">
          <p className="font-poppins text-small font-semibold text-brij-White text-right">
            SAVE <br /> &amp; EXIT
          </p>
          <IconExit />
        </div>
      </div>
    </div>
  );
}

export default observer(SlideDrawer);

interface AddButtonProps {
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

export function AddButton(props: AddButtonProps) {
  return (
    <div className="w-6 h-6 cursor-pointer" onClick={props.onClick}>
      <svg
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M36.5 26.4H26.9V36H22.1V26.4H12.5V21.6H22.1V12H26.9V21.6H36.5V26.4ZM24.5 0C21.3483 0 18.2274 0.620778 15.3156 1.82689C12.4038 3.033 9.75804 4.80083 7.52944 7.02944C3.02856 11.5303 0.5 17.6348 0.5 24C0.5 30.3652 3.02856 36.4697 7.52944 40.9706C9.75804 43.1992 12.4038 44.967 15.3156 46.1731C18.2274 47.3792 21.3483 48 24.5 48C30.8652 48 36.9697 45.4714 41.4706 40.9706C45.9714 36.4697 48.5 30.3652 48.5 24C48.5 20.8483 47.8792 17.7274 46.6731 14.8156C45.467 11.9038 43.6992 9.25804 41.4706 7.02944C39.242 4.80083 36.5962 3.033 33.6844 1.82689C30.7726 0.620778 27.6517 0 24.5 0V0Z"
          fill="#FFFEFC"
        ></path>
      </svg>
    </div>
  );
}
